// src/merch/usePrintComposite.js
//
// Runs the shared engine compositor whenever the design changes and
// returns the latest composite canvas (square, transparent outside art).
// Both the 3D texture and the flat print-area editor read from this, so
// what the customer sees in 3D is exactly what gets printed.

import { useEffect, useRef, useState } from "react";
import { snapshot, subscribe } from "valtio";
import { publishLive } from "./liveTexture";
import { buildFlattenedUV } from "../engine";
import { merch, layersForSide } from "./store";
import { getPaintCanvas } from "./paint";

export function usePrintComposite(rev, { size = 1024, delay = 40, paintRev = 0, side = null, enabled = true } = {}) {
  const [canvas, setCanvas] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled) { setCanvas(null); return; }
    const my = ++seq.current;
    const t = setTimeout(async () => {
      const s = side || merch.side;
      const layers = snapshot(merch).side === s ? snapshot(merch).layers : layersForSide(s);
      const c = await buildFlattenedUV(layers, { size, edgeClip: 0.05 });
      // overlay uncommitted drawing (active side only) so 3D shows it live
      const paint = s === merch.side ? getPaintCanvas() : null;
      if (paint) c.getContext("2d").drawImage(paint, 0, 0, size, size);
      if (my === seq.current) setCanvas(c);
    }, delay);
    return () => clearTimeout(t);
  }, [rev, size, delay, paintRev, side, enabled]);

  return canvas;
}

/**
 * Composites for EVERY zone (the 3D model shows them all). The active zone
 * re-renders on each edit (`rev`); parked zones only when they change
 * (`sideRev`: switching, copying, restoring). Returns { [zoneId]: canvas }.
 */
export function useZoneComposites(zoneIds, activeId, rev, sideRev, paintRev, { size = 1024, delay = 40 } = {}) {
  const [map, setMap] = useState({});
  const seq = useRef(0);
  const key = zoneIds.join("|");

  // active zone: layers only (the editor draws the drawing-in-progress on
  // top itself). Paused mid-drag; the editor draws live and bumps rev
  // once the drag ends.
  useEffect(() => {
    if (!activeId || merch.dragging) return;
    const my = ++seq.current;
    const t = setTimeout(async () => {
      const c = await buildFlattenedUV(snapshot(merch).layers, { size, edgeClip: 0.05 });
      if (my === seq.current) setMap((m) => ({ ...m, [activeId]: c }));
    }, delay);
    return () => clearTimeout(t);
  }, [rev, activeId, size, delay]);

  // the drawing in progress → 3D, live but throttled (~10 fps): cached
  // layers + paint buffer into one canvas, straight onto the texture
  const base = map[activeId] || null;
  const paintLive = useRef(null);
  useEffect(() => {
    if (!activeId) return;
    let last = 0, timer = null;
    const push = () => {
      timer = null; last = performance.now();
      const paint = getPaintCanvas();
      if (!paint) { if (base) publishLive(activeId, base); return; }
      if (!paintLive.current) { paintLive.current = document.createElement("canvas"); }
      const c = paintLive.current;
      if (c.width !== size) { c.width = c.height = size; }
      const ctx = c.getContext("2d");
      ctx.clearRect(0, 0, size, size);
      if (base) ctx.drawImage(base, 0, 0);
      ctx.drawImage(paint, 0, 0, size, size);
      publishLive(activeId, c);
    };
    const onPaint = () => {
      if (timer) return;
      timer = setTimeout(push, Math.max(0, 100 - (performance.now() - last)));
    };
    push(); // also re-applies the drawing when the layer composite changes
    const unsub = subscribe(merch.paint, onPaint);
    return () => { unsub(); clearTimeout(timer); };
  }, [activeId, base, size]);

  // parked zones: when they change
  useEffect(() => {
    let alive = true;
    (async () => {
      const next = {};
      for (const id of zoneIds) {
        if (id === merch.side) continue;
        const layers = layersForSide(id);
        next[id] = layers.length ? await buildFlattenedUV(layers, { size, edgeClip: 0.05 }) : null;
      }
      if (alive) setMap((m) => ({ ...m, ...next }));
    })();
    return () => { alive = false; };
  }, [sideRev, key, size]); // eslint-disable-line react-hooks/exhaustive-deps

  return map;
}

/** Full-resolution print export: the composite cropped to the print area
 *  PLUS bleed on every side (bleed = artwork past the cut line). Returns a
 *  canvas covering trim + bleed, ready for buildCutPdf. */
export async function buildPrintCanvas(printArea, sticker, bleedMm = 2, size = 2048, side = null) {
  const s = side || merch.side;
  const layers = s === merch.side ? snapshot(merch).layers : layersForSide(s);
  const full = await buildFlattenedUV(layers, { size, edgeClip: 0.05 });
  const aw = printArea.x1 - printArea.x0;
  const ah = printArea.y1 - printArea.y0;
  // bleed in normalised UV units (per axis, from true mm)
  const bx = (bleedMm / sticker.widthMm) * aw;
  const by = (bleedMm / sticker.heightMm) * ah;
  const sx = Math.round((printArea.x0 - bx) * size);
  const sy = Math.round((printArea.y0 - by) * size);
  const sw = Math.round((aw + bx * 2) * size);
  const sh = Math.round((ah + by * 2) * size);
  const out = document.createElement("canvas");
  out.width = sw;
  out.height = sh;
  out.getContext("2d").drawImage(full, sx, sy, sw, sh, 0, 0, sw, sh);
  return out;
}
